import { useCallback, useEffect, useMemo, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { useUnionRoster } from '../../union-roster/hooks/useUnionRoster.js';
import {
  getCurrentPushSubscription,
  pushSupported,
  sendUnionPush,
  subscribeToPush,
  syncCurrentPushSubscription,
  unsubscribeFromPush,
} from '../api/pushApi.js';

const DESTINATIONS = [
  { value: '/', label: '홈' },
  { value: '/#/raid', label: '레이드 현황' },
  { value: '/#/practice', label: '모의전' },
  { value: '/#/tetris', label: '테트리스' },
  { value: '/#/settlement', label: '결산' },
  { value: '/#/notice', label: '공지' },
];

export function PushSettings({ canManage = false, unionId = '' }) {
  const roster = useUnionRoster();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState('');
  const [targetMode, setTargetMode] = useState('all');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [form, setForm] = useState({ title: 'DoroParty', body: '', url: '/' });

  const eligibleMembers = useMemo(
    () => roster.members.filter((member) => member.siteMember && member.linkedUserId),
    [roster.members],
  );

  const visibleMembers = useMemo(() => {
    const query = memberQuery.trim().toLocaleLowerCase('ko-KR');
    if (!query) return eligibleMembers;
    return eligibleMembers.filter((member) => String(member.nickname || '').toLocaleLowerCase('ko-KR').includes(query));
  }, [eligibleMembers, memberQuery]);

  const refreshSubscriptionState = useCallback(async ({ syncServer = false } = {}) => {
    if (!pushSupported()) {
      setSubscribed(false);
      setSubscriptionChecked(true);
      return false;
    }
    const value = syncServer && unionId
      ? await syncCurrentPushSubscription({ unionId })
      : await getCurrentPushSubscription();
    const next = Boolean(value);
    setSubscribed(next);
    setSubscriptionChecked(true);
    return next;
  }, [unionId]);

  useEffect(() => {
    refreshSubscriptionState({ syncServer: true }).catch(() => setSubscriptionChecked(true));
    const refreshVisibleState = () => {
      if (document.visibilityState === 'visible') refreshSubscriptionState().catch(() => {});
    };
    window.addEventListener('focus', refreshVisibleState);
    document.addEventListener('visibilitychange', refreshVisibleState);
    return () => {
      window.removeEventListener('focus', refreshVisibleState);
      document.removeEventListener('visibilitychange', refreshVisibleState);
    };
  }, [refreshSubscriptionState]);

  const toggle = async () => {
    const wasSubscribed = subscribed;
    setWorking(true);
    setNotice('');
    try {
      if (wasSubscribed) await unsubscribeFromPush();
      else await subscribeToPush({ unionId });
      const next = await refreshSubscriptionState();
      if (wasSubscribed && next) throw new Error('브라우저의 알림 구독이 아직 남아 있습니다. 잠시 후 다시 시도해주세요.');
      if (!wasSubscribed && !next) throw new Error('브라우저의 알림 구독을 확인하지 못했습니다. 다시 시도해주세요.');
      setNotice(next ? '이 기기에서 DoroParty 알림을 받도록 설정했습니다.' : '이 기기의 알림을 껐습니다.');
    } catch (error) {
      await refreshSubscriptionState().catch(() => {});
      setNotice(error?.message || '알림 설정을 변경하지 못했습니다.');
    } finally {
      setWorking(false);
    }
  };

  const toggleMember = (userId) => {
    setSelectedUserIds((current) => current.includes(userId)
      ? current.filter((value) => value !== userId)
      : [...current, userId]);
  };

  const selectVisibleMembers = () => {
    setSelectedUserIds((current) => [...new Set([...current, ...visibleMembers.map((member) => member.linkedUserId)])]);
  };

  const clearSelectedMembers = () => setSelectedUserIds([]);

  const send = async () => {
    if (!form.body.trim()) return;
    if (targetMode === 'selected' && !selectedUserIds.length) {
      setNotice('알림을 보낼 멤버를 선택해주세요.');
      return;
    }

    setWorking(true);
    setNotice('');
    try {
      const result = await sendUnionPush({
        ...form,
        targetUserIds: targetMode === 'selected' ? selectedUserIds : [],
      });
      const targetLabel = targetMode === 'selected'
        ? `선택 멤버 ${Number(result.recipientUsers || 0)}명`
        : '전체 유니온';
      setNotice(`${targetLabel}의 ${Number(result.sent || 0)}개 기기에 알림을 보냈습니다.${result.failed ? ` 전송 실패 ${result.failed}건` : ''}`);
      setForm((current) => ({ ...current, body: '' }));
    } catch (error) {
      setNotice(error?.message || '알림을 보내지 못했습니다.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="push-settings">
      <header className="push-settings-heading">
        <p className="section-eyebrow">PWA & PUSH</p>
        <h1>앱·알림</h1>
        <p>DoroParty를 앱으로 설치하고 이 기기에서 유니온 알림을 받을 수 있습니다.</p>
      </header>

      <section className={`push-device-card ${subscribed ? 'active' : ''}`} aria-label="이 기기 알림 설정">
        <div className="push-device-card-copy">
          <span className="push-device-icon" aria-hidden="true"><DororaidIcon name="notice" size={22} /></span>
          <div>
            <strong>이 기기 알림</strong>
            <small>{!subscriptionChecked ? '브라우저의 알림 구독 상태를 확인하고 있습니다.' : subscribed ? '현재 doroparty.com 알림을 받고 있습니다.' : '알림 권한을 켜면 푸시를 받을 수 있습니다.'}</small>
          </div>
        </div>
        {!pushSupported() ? (
          <p className="push-settings-inline-notice">이 브라우저에서는 웹 알림을 사용할 수 없습니다. iPhone에서는 홈 화면에 추가한 뒤 앱으로 실행해주세요.</p>
        ) : (
          <button className="push-device-toggle" type="button" disabled={working || !subscriptionChecked} onClick={toggle}>
            {!subscriptionChecked ? '확인 중…' : working ? '처리 중…' : subscribed ? '알림 끄기' : '알림 켜기'}
          </button>
        )}
      </section>

      {notice ? <p className="push-settings-notice" role="status">{notice}</p> : null}

      {canManage ? (
        <section className="push-send-panel" aria-label="유니온 알림 발송">
          <header className="push-send-panel-heading">
            <div>
              <span>UNION PUSH</span>
              <h2>유니온 알림</h2>
            </div>
            <small>{targetMode === 'selected' ? `${selectedUserIds.length}명 선택` : '전체 발송'}</small>
          </header>

          <div className="push-form-grid">
            <label className="push-field">
              <span>제목</span>
              <input value={form.title} maxLength={60} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            </label>
            <label className="push-field">
              <span>이동 위치</span>
              <select value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })}>
                {DESTINATIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label className="push-field push-message-field">
              <span>알림 내용</span>
              <textarea value={form.body} maxLength={240} onChange={(event) => setForm({ ...form, body: event.target.value })} placeholder="보낼 내용을 입력하세요." />
              <small>{form.body.length}/240</small>
            </label>
          </div>

          <section className="push-target-section" aria-label="알림 대상 선택">
            <div className="push-target-mode" role="group" aria-label="알림 대상">
              <button className={targetMode === 'all' ? 'active' : ''} type="button" onClick={() => setTargetMode('all')}>전체 유니온</button>
              <button className={targetMode === 'selected' ? 'active' : ''} type="button" onClick={() => setTargetMode('selected')}>멤버 선택</button>
            </div>

            {targetMode === 'selected' ? (
              <div className="push-member-picker">
                <div className="push-member-picker-tools">
                  <input type="search" value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="닉네임 검색" aria-label="알림 멤버 검색" />
                  <button type="button" onClick={selectVisibleMembers}>전체 선택</button>
                  <button type="button" onClick={clearSelectedMembers}>선택 해제</button>
                </div>
                <div className="push-member-list">
                  {roster.loading ? <p>멤버를 불러오는 중…</p> : visibleMembers.length ? visibleMembers.map((member) => (
                    <label className={selectedUserIds.includes(member.linkedUserId) ? 'selected' : ''} key={member.linkedUserId}>
                      <input type="checkbox" checked={selectedUserIds.includes(member.linkedUserId)} onChange={() => toggleMember(member.linkedUserId)} />
                      <span>{member.nickname}</span>
                    </label>
                  )) : <p>조건에 맞는 사이트 가입 멤버가 없습니다.</p>}
                </div>
              </div>
            ) : null}
          </section>

          <button className="push-send-button" type="button" disabled={working || !form.body.trim() || (targetMode === 'selected' && !selectedUserIds.length)} onClick={send}>
            <DororaidIcon name="notice" size={19} />
            {working ? '보내는 중…' : <span>보내기</span>}
          </button>
        </section>
      ) : null}
    </section>
  );
}
